import { useState, useEffect, useRef, useCallback, useMemo } from "react";

import ReportMarkdown from "./ReportMarkdown";
import CitationFooter from "./CitationFooter";
import { extractAnnotations } from "./utils/reportUtils";

const ReportDocument = ({
  title,
  content,
  citations,
  streamingContent,
  isStreaming,
  isUndoing,
  undoTargetContent,
  undoTargetTitle,
  undoTargetCitations,
  onUndoComplete,
  reportOptions,
  onSourceClick,
  activeSourceId,
  citationInteractions,
}) => {
  const scrollRef = useRef(null);
  const userScrolledRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const lastContentHeightRef = useRef(0);

  // ── Undo animation ────────────────────────────────────────────────────
  const [animContent, setAnimContent] = useState(null);
  const [animPhase, setAnimPhase] = useState(null);
  const eraseRef = useRef(null);
  const typeRef = useRef(null);

  useEffect(() => {
    if (!isUndoing) return;
    const source = content || "";
    let idx = source.length;
    setAnimContent(source);
    setAnimPhase("erasing");
    eraseRef.current = setInterval(() => {
      idx -= 12;
      if (idx <= 0) {
        clearInterval(eraseRef.current);
        eraseRef.current = null;
        setAnimContent("");
        setAnimPhase("typing");
      } else setAnimContent(source.slice(0, idx));
    }, 8);
    return () => {
      clearInterval(eraseRef.current);
      clearInterval(typeRef.current);
    };
  }, [isUndoing]);

  useEffect(() => {
    if (animPhase !== "typing") return;
    const target = undoTargetContent || "";
    if (!target.length) {
      setAnimContent("");
      setAnimPhase(null);
      onUndoComplete?.();
      return;
    }
    let idx = 0;
    typeRef.current = setInterval(() => {
      idx += 8;
      if (idx >= target.length) {
        clearInterval(typeRef.current);
        typeRef.current = null;
        setAnimContent(null);
        setAnimPhase(null);
        onUndoComplete?.();
      } else setAnimContent(target.slice(0, idx));
    }, 8);
    return () => clearInterval(typeRef.current);
  }, [animPhase, undoTargetContent, onUndoComplete]);

  /* Scroll stability: keep viewport anchored during content growth */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const newHeight = el.scrollHeight;
    const heightDelta = newHeight - lastContentHeightRef.current;
    if (heightDelta > 0 && lastScrollTopRef.current > 0) {
      el.scrollTop = lastScrollTopRef.current + heightDelta;
    }
    lastContentHeightRef.current = newHeight;
  }, [streamingContent, animContent, isStreaming, animPhase, content]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    lastScrollTopRef.current = el.scrollTop;
    lastContentHeightRef.current = el.scrollHeight;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    userScrolledRef.current = !atBottom;
  }, []);

  const displayTitle =
    isUndoing && animPhase === "typing" && undoTargetTitle
      ? undoTargetTitle
      : title;
  const displayCitations = isUndoing ? [] : citations;

  let displayContent = "";
  let displayStreaming = false;
  if (animContent !== null) {
    displayContent = animContent;
    displayStreaming = true;
  } else if (isStreaming && streamingContent) {
    displayContent = streamingContent;
    displayStreaming = true;
  } else {
    displayContent = content;
    displayStreaming = false;
  }

  const showLineNumbers = reportOptions?.line_numbers || false;

  const annotations = useMemo(
    () => extractAnnotations(displayContent),
    [displayContent],
  );

  const hasContent = displayContent && displayContent.trim().length > 0;

  const cleanContent = useMemo(() => {
    if (!displayContent) return "";
    return displayContent.replace(/<!--\s*@pagebreak\s*-->/gi, "\n\n---\n\n");
  }, [displayContent]);

  return (
    <div className="h-full flex flex-col bg-zinc-100/50 dark:bg-zinc-900/50">
      <div className="flex-shrink-0 pt-14" />

      <div
        className="flex-1 min-h-0 overflow-y-auto"
        ref={scrollRef}
        onScroll={handleScroll}
      >
        {!hasContent ? (
          <div className="h-full flex flex-col items-center justify-center px-8 text-center">
            {!isStreaming && !isUndoing && (
              <p className="text-[14px] text-zinc-400 dark:text-zinc-600 leading-relaxed">
                Start a conversation to begin building your report.
              </p>
            )}
          </div>
        ) : (
          <div className="flex justify-center px-6 pb-12">
            <div
              className={[
                "w-full max-w-[816px] bg-white dark:bg-zinc-950",
                "rounded shadow-sm border border-zinc-200/70 dark:border-zinc-800",
                showLineNumbers ? "px-6 py-10" : "px-12 py-10",
                "relative",
              ].join(" ")}
            >
              {/* Line numbers gutter */}
              {showLineNumbers && !displayStreaming && (
                <div className="absolute left-0 top-10 bottom-10 w-10 border-r border-zinc-100 dark:border-zinc-800 flex flex-col text-right pr-2 pt-0.5 select-none pointer-events-none overflow-hidden">
                  {cleanContent.split("\n").map((_, li) => (
                    <span
                      key={li}
                      className="text-[10px] leading-[1.8] text-zinc-300 dark:text-zinc-700 font-mono"
                    >
                      {(reportOptions?.start_line || 1) + li}
                    </span>
                  ))}
                </div>
              )}

              <div className={showLineNumbers ? "ml-8" : ""}>
                <div
                  data-report-content
                  className="text-[15px] leading-[1.8] text-zinc-700 dark:text-zinc-300"
                >
                  <ReportMarkdown
                    text={cleanContent}
                    citations={displayCitations}
                    streaming={displayStreaming}
                    onSourceClick={onSourceClick}
                    activeSourceId={activeSourceId}
                    citationInteractions={citationInteractions}
                  />
                  {displayStreaming && displayContent && (
                    <span className="inline-block w-[3px] h-[18px] ml-0.5 bg-zinc-800 dark:bg-zinc-300 rounded-full animate-pulse align-middle" />
                  )}
                  {!displayStreaming && displayCitations.length > 0 && (
                    <CitationFooter
                      citations={displayCitations}
                      onSourceClick={onSourceClick}
                      activeSourceId={activeSourceId}
                      citationInteractions={citationInteractions}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Margin annotations overlay */}
            {annotations.length > 0 && !displayStreaming && (
              <div className="fixed right-4 top-20 w-48 space-y-2 z-10 hidden xl:block">
                {annotations.map((a, i) => (
                  <div
                    key={i}
                    className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded px-2.5 py-1.5 text-[11px] text-amber-800 dark:text-amber-200"
                  >
                    {a.text}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportDocument;
